import { memo } from "react";
import Head from "next/head";
import { formatPrice } from "@/helpers/pdpHelpers/priceHelpers";
import { useRouter } from "next/compat/router";

const getStructuredData = (productDetails, colors, tagData, firstImage) => {
  if (productDetails) {
    let category = null;
    if (tagData?.categories_lvl1 && tagData.categories_lvl1.length > 0) {
      // if multiple level1 categories use latest one
      const level1Categories =
        tagData.categories_lvl1[tagData.categories_lvl1.length - 1];
      category = level1Categories[level1Categories.length - 1];
    } else if (tagData?.categories_lvl0) {
      category = tagData.categories_lvl0[0];
    }

    const offers = productDetails.variants.edges.map((variant) => {
      const sku = variant.node.sku;
      return {
        "@type": "Offer",
        ...(sku && { sku: sku }),
        url: `${process.env.NETLIFY_URL}/products/${productDetails.handle}`,
        priceCurrency: "USD",
        price: formatPrice(variant.node.priceV2.amount, false),
        availability: variant.node.availableForSale
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
      };
    });

    const sku = productDetails.variants.edges[0].node.sku;

    const structuredData = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: productDetails.title,
      ...(firstImage && { image: firstImage.url }),
      description: productDetails.description,
      brand: {
        "@type": "Brand",
        name: productDetails.vendor,
      },
      ...(sku && { sku: sku }),
      productID: productDetails?.id?.replace("gid://shopify/Product/", ""),
      ...(category && { category: category }),
      ...(colors && { color: colors }),
      offers: offers,
    };

    return structuredData;
  }
  return null;
};

const getOgTags = (productDetails, firstImageGroup) => {
  if (productDetails) {
    const imageOgTags = [];

    if (firstImageGroup) {
      firstImageGroup.forEach((image) => {
        if (image.url) {
          imageOgTags.push({ "og:image": image.previewImage.url });
          imageOgTags.push({ "og:image:secure_url": image.previewImage.url });
          imageOgTags.push({ "og:image:width": "1200" });
          imageOgTags.push({ "og:image:height": "628" });
          imageOgTags.push({
            "og:image:alt": productDetails.title,
          });
        }
      });
    }

    const tags = [
      { "og:type": "product" },
      { "og:title": productDetails.title },
      {
        "og:url": `${process.env.NETLIFY_URL}/products/${productDetails.handle}`,
      },
      {
        "og:description": productDetails.description,
      },
      ...(imageOgTags || []),
    ];

    return tags;
  }
  return null;
};

const Seo = ({
  productDetails,
  colors,
  tagData,
  productImageGroups,
}) => {
  const seoData = productDetails.seo;
  
  let firstImageGroup = null;
  let firstImage = null;
  if (productImageGroups && Object.keys(productImageGroups).length > 0) {
    firstImageGroup = productImageGroups[Object.keys(productImageGroups)[0]];
    firstImage = firstImageGroup[0];
  }

  const structuredData = getStructuredData(
    productDetails,
    colors,
    tagData,
    firstImage
  );

  const ogTags = getOgTags(productDetails, firstImageGroup);
  const router = useRouter();
  // Canonical URL checks if  its a homepage it will references the primary URL else it will append routerpath to the baseURL
  // split("?")= basically clean up the Url Parameter like "?query="
  const canonicalUrl = (`${process.env.NETLIFY_URL}` + (router.asPath === "/" ? "": router.asPath)).split("?")[0];

  return (
    <Head>
      <>
        {/* canonical links basically helps to  prevent parameters to appear on Google search results and also improves SEO */}
        <link rel="canonical" href={canonicalUrl} />
        <title>{seoData.title || `${productDetails.vendor} ${productDetails.title} | OLIVELA`}</title>
        <meta name="title" content={seoData.title || `${productDetails.vendor} ${productDetails.title} | OLIVELA`}></meta>
        <meta name="description" content={seoData.description || `${productDetails.description}`}></meta>
        {ogTags.map((tag, index) => {
          return (
            <meta
              key={index}
              property={Object.keys(tag)[0]}
              content={Object.values(tag)[0]}
            ></meta>
          );
        })}
        <meta name="twitter:card" content="summary_large_image"></meta>
        <meta
          name="twitter:url"
          content={`${process.env.NETLIFY_URL}/products/${productDetails.handle}`}
        ></meta>
        <meta name="twitter:title" content={productDetails.title}></meta>
        <meta
          name="twitter:description"
          content={productDetails.description}
        ></meta>
        {firstImage && (
          <meta
            name="twitter:image"
            content={firstImage?.previewImage?.url}
          ></meta>
        )}
        <script
          id="product-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:  JSON.stringify(structuredData) }}
        />
      </>
    </Head>
  );
};

export default memo(Seo);
